import { useAuth0 } from '@auth0/auth0-react'
import {
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { QueryKeys } from '../../types'
import {
  deleteUser,
  detailUser,
  getUser,
  getUserFollowUpContent,
  getUserHealthPlan,
  getUserHealthSummary,
  getUsers,
  getUserScreeningDataByQuestionId,
  getUsersFromOrganisation,
  inviteUser,
  logIn,
  postSingleUser,
  postUsersWithCvs,
  updateUser,
} from './users'
import {
  User,
  UsersFromOrganisationDb,
  CreateUsersResponse,
  InviteUserRequest,
  InviteUserResponse,
  UserScreeningStatisticsResponse,
  Permission,
} from './types'
import { StoredHealthPlan } from '@estercare/ester-shared'
import { SingleUserFormDataType } from '../../../../utils/validation'

export const useGetUsersQuery = (): UseQueryResult<User[], Error> => {
  const { getAccessTokenSilently } = useAuth0()

  return useQuery({
    queryKey: [QueryKeys.Users, 'private'],
    queryFn: async (): Promise<User[]> => {
      const accessToken = await getAccessTokenSilently()
      return await getUsers({ accessToken })
    },
  })
}

export const useGetUserQuery = ({
  id,
  options,
}: {
  id: string
  options?: Omit<UndefinedInitialDataOptions<User>, 'queryKey' | 'queryFn'>
}): UseQueryResult<User, Error> => {
  const { getAccessTokenSilently } = useAuth0()
  return useQuery({
    queryKey: [QueryKeys.User, 'private'],
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently()
      return await getUser({ accessToken, id })
    },
    ...options,
  })
}

export const useGetUsersFromOrganisationQuery = (
  organisationId?: string
): UseQueryResult<UsersFromOrganisationDb[], Error> => {
  const { getAccessTokenSilently } = useAuth0()
  if (!organisationId) throw new Error('Missing required organisationId')

  return useQuery({
    queryKey: [QueryKeys.OrganisationUsers, 'private'],
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently()
      const users: UsersFromOrganisationDb[] = await getUsersFromOrganisation({ accessToken, organisationId })

      return users
    },
    enabled: !!organisationId,
  })
}

export const useDeleteUserMutation = () => {
  const { getAccessTokenSilently } = useAuth0()

  return useMutation({
    mutationKey: [QueryKeys.User, 'private'],
    mutationFn: async (id: string) => {
      const accessToken = await getAccessTokenSilently()
      await deleteUser({ accessToken, id })
    },
  })
}

export const usePostLoginQuery = ({
  options,
}: {
  options?: Omit<UseMutationOptions<User>, 'mutationKey' | 'mutationFn'>
}) => {
  const { getAccessTokenSilently } = useAuth0()

  return useMutation({
    mutationKey: [QueryKeys.User, 'private'],
    mutationFn: async () => {
      const accessToken = await getAccessTokenSilently()
      return await logIn({ accessToken })
    },
    ...options,
  })
}

export const usePostUsersWithCvs = () => {
  const queryClient = useQueryClient()
  const { getAccessTokenSilently } = useAuth0()

  return useMutation<CreateUsersResponse, Error, { usersFileData: FormData }>({
    mutationKey: [QueryKeys.User, 'private', 'uploadUsers'],
    mutationFn: async ({ usersFileData }) => {
      const accessToken = await getAccessTokenSilently()
      return await postUsersWithCvs({ accessToken, usersFileData })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.OrganisationUsers, 'private'] })
    },
    onError: (error) => {
      throw new Error(`Failed to upload users ${error}`)
    },
  })
}

export const usePostUsers = () => {
  const { getAccessTokenSilently } = useAuth0()

  return useMutation({
    mutationKey: [QueryKeys.User, 'private', 'postSingleUser'],
    mutationFn: async ({ users }: { users: SingleUserFormDataType[] }) => {
      const accessToken = await getAccessTokenSilently()
      return await postSingleUser({ accessToken, users })
    },
  })
}

export const useGetUserHealthSummaryQuery = (): UseQueryResult<string> => {
  const { getAccessTokenSilently } = useAuth0()
  return useQuery({
    queryKey: [QueryKeys.HealthSummary, 'private'],
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently()
      return await getUserHealthSummary({ accessToken })
    },
  })
}

export const useGetUserFollowUpQueryContent = (): UseQueryResult<string> => {
  const { getAccessTokenSilently } = useAuth0()
  return useQuery({
    queryKey: [QueryKeys.followUp, 'private'],
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently()
      return await getUserFollowUpContent({ accessToken })
    },
  })
}

export const useGetUserScreeningStatisticsQuery = (): UseQueryResult<UserScreeningStatisticsResponse> => {
  const { getAccessTokenSilently } = useAuth0()
  return useQuery({
    queryKey: [QueryKeys.ScreeningStatistics, 'private'],
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently()
      return await getUserScreeningDataByQuestionId(accessToken)
    },
  })
}

export const useGetUserHealthPlanQuery = (): UseQueryResult<StoredHealthPlan> => {
  const { getAccessTokenSilently } = useAuth0()
  return useQuery({
    queryKey: [QueryKeys.HealthPlan, 'private'],
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently()
      return await getUserHealthPlan({ accessToken })
    },
  })
}

export const useUpdateUserMutation = () => {
  const queryClient = useQueryClient()
  const { getAccessTokenSilently } = useAuth0()

  return useMutation({
    mutationKey: [QueryKeys.User, 'private', 'patchUser'],
    mutationFn: async ({
      id,
      newUserData,
    }: {
      id: string
      newUserData: Partial<Omit<User, 'permission'>> & { permission?: Permission }
    }) => {
      const accessToken = await getAccessTokenSilently()
      return await updateUser({ accessToken, id, newUserData })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.OrganisationUsers, 'private'] })
    },
    onError: (error) => {
      throw new Error(`Failed to upload users ${error}`)
    },
  })
}

export const useInviteUserMutation = () => {
  const { getAccessTokenSilently } = useAuth0()

  return useMutation<InviteUserResponse, Error, InviteUserRequest>({
    mutationKey: [QueryKeys.User, 'private', 'inviteUser'],
    mutationFn: async (data) => {
      const accessToken = await getAccessTokenSilently()
      return await inviteUser({ accessToken, data })
    },
  })
}

export const useDetailUserMutation = () => {
  const queryClient = useQueryClient()
  const { getAccessTokenSilently } = useAuth0()

  return useMutation({
    mutationKey: [QueryKeys.User, 'private', 'DetailUser'],
    mutationFn: async ({ newUserData }: { newUserData: Partial<User> & { personalNumber?: string } }) => {
      const accessToken = await getAccessTokenSilently()
      return await detailUser({ accessToken, newUserData })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.OrganisationUsers, 'private'] })
    },
    onError: (error) => {
      throw new Error(`Failed to patch data to user ${error}`)
    },
  })
}
